<template>
    <!--
        THE SHELL'S LEFT COLUMN: a brand row, the grouped menu, and the signed-in user at the
        bottom — the three things a console's sidebar is for, in the order they are looked at.

        It paints `--ui-bg-chrome` — the project surface `theme/theme.css` declares and the
        house theme separates from the canvas. THE FRAME IS ONE SURFACE, and in this theme that
        surface is the PANEL colour in both modes: white in light, and in dark the same mix the
        cards take, both standing on a darker canvas. The hairline on the right edge is the seam
        between the frame and the page.

        Opaque, not a tint over the canvas: on mobile this column is the overlay.

        DENSITY matches the company web (team directive): the band is `p-2` and the rows
        `py-1.5` rather than the template's `px-3 py-4 gap-5` / `py-2`, so the two frontends'
        menus read alike. The band has no extra top padding: there is no section header for it
        to sit under (see `layout.ts`).

        PROJECT DEVIATION: the desktop collapse (rail) mode of the shell template was dropped —
        the shell contract here is a fixed 64-wide column on desktop and a drawer on mobile; the
        template's old `sidebar-collapsed` localStorage key is therefore orphaned and unread.
    -->
    <nav class="flex flex-col w-64 h-dvh shrink-0 border-r border-default bg-(--ui-bg-chrome)">
        <!-- PROJECT DEVIATION: brand row is an icon + one line, not the template's `/logo.svg`
             + tagline — there is no logo asset for itoms yet (same as the company web). -->
        <div class="h-(--ui-header-height) shrink-0 flex items-center gap-2.5 px-4 border-b border-default">
            <UIcon name="i-lucide-cpu" class="size-6 shrink-0 text-primary" />
            <p class="flex-1 min-w-0 truncate text-sm font-semibold text-highlighted">{{ $t('brand.product_name') }}</p>

            <!--
                THE WAY OUT, on mobile only. Open, this column IS the overlay and it covers the
                topbar's hamburger, so the affordance that opened the menu cannot also close it.
                The X lives where the eye already is — in the brand row, opposite the mark.
            -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-x"
                :aria-label="$t('layout.close_menu')"
                class="-mr-1 shrink-0"
                @click="closeMenu"
            />
        </div>

        <div class="flex flex-col gap-4 flex-1 overflow-y-auto p-2">
            <div v-for="group in groups" :key="group.key" class="flex flex-col gap-1">
                <p
                    v-if="group.labelKey"
                    class="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-widest text-muted"
                >{{ $t(group.labelKey) }}</p>

                <UNavigationMenu
                    orientation="vertical"
                    :items="group.items"
                    :ui="{ link: 'py-1.5' }"
                />
            </div>
        </div>

        <!-- The signed-in user, where the shell puts it, with the sign-out action. -->
        <div class="shrink-0 border-t border-default p-2">
            <UDropdownMenu :items="userMenu" :content="{ align: 'start', side: 'top' }" class="w-full">
                <button
                    type="button"
                    class="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-elevated transition-colors"
                >
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                        {{ initials }}
                    </span>

                    <span class="min-w-0 flex-1 leading-tight">
                        <span class="block truncate text-sm font-medium text-highlighted">{{ fullName }}</span>
                        <span class="block truncate font-mono text-[10px] uppercase tracking-widest text-muted">{{ userData?.email }}</span>
                    </span>

                    <UIcon name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
                </button>
            </UDropdownMenu>
        </div>
    </nav>
</template>

<script lang="ts" setup>
    import type { NavigationMenuItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute } from 'vue-router'

    import { logout } from '@/libs/auth'
    import { NAV_GROUPS, useLayout } from '@/layouts/layout'
    import type { NavItem } from '@/layouts/layout'
    import { useAppStore } from '@/stores/app'

    /**
     * The menu. The MODEL lives in `layouts/layout.ts` (`NAV_GROUPS`); this component only
     * turns it into `NavigationMenuItem`s: permission gating, the live badge, the active row.
     *
     * THE ACTIVE ROW IS A SOFT PRIMARY PLATE: one class per item turns the `pill` variant's
     * neutral `before:bg-elevated` into `before:bg-primary/10` (appended after the variant's
     * own class, so tailwind-merge keeps the later utility).
     *
     * `active` is set explicitly rather than left to the router, so a DETAIL route
     * (`/project/:id`, `/invoice/:id`) keeps its section lit — that is what `NavItem.routes` is for.
     */
    const { t }                   = useI18n()
    const route                   = useRoute()
    const appStore                = useAppStore()
    const { isMobile, closeMenu } = useLayout()

    const userData = computed(() => appStore.userData)

    /** The portal permission gate. */
    const visible = (item: NavItem): boolean =>
        item.permission === undefined || appStore.hasPermission(item.permission)

    /** The current route belongs to this entry — by route name, or by path prefix for a detail URL. */
    const isActive = (item: NavItem): boolean => {
        if (item.routes?.includes(String(route.name ?? ''))) return true
        if (!item.to) return false

        return route.path === item.to || route.path.startsWith(`${item.to}/`)
    }

    const label = (item: NavItem): string => t(item.labelKey)

    /** The live count, hidden at 0 — the legacy menu's blue chip (#8888ff) as the `info` colour. */
    const badgeOf = (item: NavItem) => {
        const count = item.badge?.() ?? 0
        return count > 0 ? { label: String(count), color: 'info' as const } : undefined
    }

    /**
     * One entry. A parent renders only when at least one of its children survives the
     * permission gate — an empty accordion is a row that does nothing — and it opens itself
     * when the current route is inside it.
     */
    const toMenuItem = (item: NavItem): NavigationMenuItem | null => {
        if (!visible(item)) return null

        if (item.children) {
            const children = item.children.map(toMenuItem).filter((c): c is NavigationMenuItem => c !== null)
            if (children.length === 0) return null

            return {
                label:       label(item),
                icon:        item.icon,
                badge:       badgeOf(item),
                defaultOpen: item.children.some(isActive),
                children,
            }
        }

        const active = isActive(item)

        return {
            label:    label(item),
            icon:     item.icon,
            to:       item.to,
            active,
            class:    active ? 'before:bg-primary/10' : '',
            badge:    badgeOf(item),
            onSelect: () => { closeMenu() },
        }
    }

    const groups = computed((): { key: string, labelKey: string | null, items: NavigationMenuItem[] }[] =>
        NAV_GROUPS
            .map((group, index) => ({
                key:      group.labelKey ?? `group-${index}`,
                labelKey: group.labelKey,
                items:    group.items.map(toMenuItem).filter((i): i is NavigationMenuItem => i !== null),
            }))
            .filter((group) => group.items.length > 0))

    const fullName = computed((): string =>
        `${userData.value?.name ?? ''} ${userData.value?.l_name ?? ''}`.trim())

    const initials = computed((): string => fullName.value
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(''))

    const userMenu = computed(() => [[{
        label:    t('nav.logout'),
        icon:     'i-lucide-log-out',
        color:    'error' as const,
        onSelect: () => { void logout() },
    }]])
</script>
